import React, { useState, useEffect } from 'react';
import { authAPI, orgAPI } from '../services/api';
import { useAuth } from '../services/auth';
import { useOrgRole } from '../hooks/useOrgRole';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '₹0',
    period: '/mo',
    color: '#64748b',
    features: ['1 cloud provider', '3 deployments', '1 server', 'Community support', 'Basic monitoring'],
    cta: 'Downgrade',
    recommended: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    price: '₹1',
    period: '/mo',
    color: '#6366f1',
    features: ['3 cloud providers', '20 deployments', '10 servers', 'Email support', 'Advanced monitoring', 'Cost optimizer', 'Invoice export'],
    cta: 'Upgrade →',
    recommended: false,
    badge: '🔥 Best for testing',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '₹2',
    period: '/mo',
    color: '#10b981',
    features: ['All cloud providers', 'Unlimited deployments', 'Unlimited servers', 'Priority support', 'Physical server support', 'AI cost optimizer', 'Team access', 'API access', 'Custom integrations'],
    cta: 'Upgrade →',
    recommended: true,
    badge: '⭐ Most Popular',
  },
];
const PLAN_COMPARE = [
  { feature: 'Cloud Providers',     free: '1',        starter: '3',         pro: 'All (13+)' },
  { feature: 'Deployments',         free: '3',        starter: '20',        pro: 'Unlimited' },
  { feature: 'Servers',             free: '1',        starter: '10',        pro: 'Unlimited' },
  { feature: 'Physical Servers',    free: '❌',       starter: '❌',        pro: '✅' },
  { feature: 'Cost Optimizer',      free: '❌',       starter: '✅',        pro: '✅ (AI)' },
  { feature: 'Invoice Export',      free: '❌',       starter: '✅',        pro: '✅' },
  { feature: 'Monitor & Alerts',    free: 'Basic',    starter: 'Advanced',  pro: 'Real-time' },
  { feature: 'API Access',          free: '❌',       starter: '❌',        pro: '✅' },
  { feature: 'Team Members',        free: '1',        starter: '3',         pro: 'Unlimited' },
  { feature: 'Support',             free: 'Community',starter: 'Email',     pro: 'Priority 24/7' },
];

const ROLE_OPTIONS = [
  { id: 'admin',   label: 'Admin — full access except ownership' },
  { id: 'support', label: 'Support — manage deployments, servers, clouds & integrations' },
  { id: 'viewer',  label: 'Viewer — read-only access to everything' },
  { id: 'billing', label: 'Billing — view costs & manage invoices/subscription' },
  { id: 'monitor', label: 'Monitor — server health & alerts only' },
];

export default function Settings() {
  const { user, setUser } = useAuth();
  const { organization, role, can } = useOrgRole();
  const [name,    setName]    = useState(user?.name || '');
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState(null);
  const [tab,     setTab]     = useState('profile');
  const [currentPlan, setCurrentPlan] = useState(user?.plan || 'free');

  const [org, setOrg] = useState(organization);
  const [orgName, setOrgName] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);

  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  const loadOrg = async () => {
    try {
      const r = await orgAPI.me();
      if (r.data.success && r.data.data) {
        setOrg(r.data.data.organization);
        const detail = await orgAPI.get(r.data.data.organization.id);
        if (detail.data.success) setMembers(detail.data.data.members || []);
      }
    } catch { /* not in an org yet */ }
  };

  useEffect(() => { if (tab === 'team') loadOrg(); }, [tab]);

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) return;
    setCreatingOrg(true);
    try {
      const r = await orgAPI.create({ name: orgName.trim() });
      if (r.data.success) {
        showToast('Organization created!');
        setOrgName('');
        loadOrg();
      } else {
        showToast('Could not create organization', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not create organization', 'error');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!org || !inviteEmail.trim()) return;
    setInviting(true);
    try {
      const r = await orgAPI.invite(org.id, { email: inviteEmail.trim(), role: inviteRole });
      if (r.data.success) {
        showToast('Invite sent!');
        setInviteEmail('');
        loadOrg();
      } else {
        showToast(r.data.error || 'Could not send invite', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not send invite', 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      const r = await orgAPI.updateRole(org.id, memberId, newRole);
      if (r.data.success) { showToast('Role updated'); loadOrg(); }
      else showToast(r.data.error || 'Could not update role', 'error');
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not update role', 'error');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      const r = await orgAPI.removeMember(org.id, memberId);
      if (r.data.success) { showToast('Member removed'); loadOrg(); }
      else showToast(r.data.error || 'Could not remove member', 'error');
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not remove member', 'error');
    }
  };

  const handleUpgrade = async (planId) => {
    if (planId === 'free') {
      try {
        const r = await authAPI.upgradePlan({ plan: 'free' });
        if (r.data.success) {
          setCurrentPlan('free');
          if (setUser) setUser(r.data.data);
          showToast('Switched to Free plan');
        } else {
          showToast('Could not switch plan', 'error');
        }
      } catch {
        showToast('Could not switch plan', 'error');
      }
      return;
    }
    try {
      const orderRes = await authAPI.createOrder({ plan: planId });
      if (!orderRes.data.success) {
        showToast('Could not start payment', 'error');
        return;
      }
      const { orderId, amount, currency, keyId } = orderRes.data;

      const options = {
        key: keyId,
        amount,
        currency,
        name: 'CloudUnify',
        description: `Upgrade to ${planId} plan`,
        order_id: orderId,
        handler: async (response) => {
          try {
            const verifyRes = await authAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: planId,
            });
            if (verifyRes.data.success) {
              setCurrentPlan(planId);
              if (setUser) setUser(verifyRes.data.data);
              showToast(`Upgraded to ${planId} plan!`);
            } else {
              showToast('Payment verification failed', 'error');
            }
          } catch {
            showToast('Payment verification failed', 'error');
          }
        },
        prefill: { name: user?.name || '', email: user?.email || '' },
        theme: { color: '#6366f1' },
        modal: {
          ondismiss: () => showToast('Payment cancelled', 'error'),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => showToast('Payment failed', 'error'));
      rzp.open();
    } catch {
      showToast('Could not start payment', 'error');
    }
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const r = await authAPI.profile({ name });
      if (r.data.success) { showToast('Profile updated!'); if (setUser) setUser(r.data.data); }
    } catch { showToast('Failed to update', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {toast && (
        <div style={{position:'fixed',top:20,right:20,zIndex:9999,background:toast.type==='success'?'rgba(16,185,129,0.15)':'rgba(239,68,68,0.15)',border:`1px solid ${toast.type==='success'?'#10b981':'#ef4444'}`,borderRadius:10,padding:'12px 20px',color:toast.type==='success'?'#10b981':'#ef4444',fontWeight:600,backdropFilter:'blur(10px)'}}>
          {toast.type==='success'?'✅':'❌'} {toast.msg}
        </div>
      )}

      <div style={{marginBottom:24}}>
        <h1 style={{margin:0}}>⚙ Settings</h1>
        <p style={{color:'#8899b4',margin:'4px 0 0'}}>Manage your account preferences</p>
      </div>

      <div style={{display:'flex',gap:4,marginBottom:24,borderBottom:'1px solid var(--border)',paddingBottom:0,flexWrap:'wrap'}}>
        {[['profile','👤 Profile'],['plans','💳 Plans & Billing'],['compare','📊 Compare Plans'],['team','👥 Team'],['danger','⚠️ Danger Zone']].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{padding:'10px 18px',background:'none',border:'none',borderBottom:`2px solid ${tab===id?'#6366f1':'transparent'}`,color:tab===id?'#6366f1':'#8899b4',fontSize:13,fontWeight:600,cursor:'pointer',marginBottom:-1}}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <div style={{background:'var(--bg-card)',borderRadius:14,padding:24,border:'1px solid var(--border)',maxWidth:500}}>
          <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:24}}>
            <div style={{width:64,height:64,borderRadius:'50%',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,fontWeight:800,color:'#fff'}}>
              {(user?.name||'A')[0].toUpperCase()}
            </div>
            <div>
              <div style={{fontWeight:700,fontSize:17}}>{user?.name}</div>
              <div style={{color:'#8899b4',fontSize:13}}>{user?.email}</div>
              <span style={{fontSize:11,padding:'2px 10px',borderRadius:12,background:'rgba(99,102,241,0.15)',color:'#6366f1',fontWeight:700,border:'1px solid rgba(99,102,241,0.3)'}}>
                {currentPlan} plan{role ? ` · ${role}` : ''}
              </span>
            </div>
          </div>
          <form onSubmit={handleSave}>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Display Name</label>
              <input className="form-control" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" required/>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:13,color:'#8899b4',display:'block',marginBottom:6}}>Email</label>
              <input className="form-control" value={user?.email||''} disabled style={{opacity:0.6}}/>
            </div>
            <button type="submit" disabled={saving} style={{padding:'10px 24px',borderRadius:8,background:'#6366f1',border:'none',color:'#fff',fontWeight:700,cursor:'pointer'}}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {tab === 'plans' && (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:20,marginBottom:32}}>
            {PLANS.map(plan => (
              <div key={plan.id} style={{background:'var(--bg-card)',borderRadius:14,padding:24,border:plan.recommended?`2px solid ${plan.color}`:'1px solid var(--border)',position:'relative',transition:'all 0.2s',boxShadow:plan.recommended?`0 0 24px ${plan.color}33`:undefined}}>
                {plan.badge && (
                  <div style={{position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',background:plan.color,color:'#fff',fontSize:11,fontWeight:700,padding:'3px 14px',borderRadius:12,whiteSpace:'nowrap'}}>
                    {plan.badge}
                  </div>
                )}
                <div style={{fontSize:17,fontWeight:800,marginBottom:4}}>{plan.name}</div>
                <div style={{display:'flex',alignItems:'baseline',gap:2,marginBottom:16}}>
                  <span style={{fontSize:32,fontWeight:900,color:plan.color}}>{plan.price}</span>
                  <span style={{fontSize:13,color:'#8899b4'}}>{plan.period}</span>
                </div>
                <div style={{marginBottom:20}}>
                  {plan.features.map(f=>(
                    <div key={f} style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#8899b4',marginBottom:7}}>
                      <span style={{color:plan.color,fontWeight:700}}>✓</span> {f}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => currentPlan!==plan.id && handleUpgrade(plan.id)}
                  disabled={currentPlan===plan.id}
                  style={{width:'100%',padding:'10px',borderRadius:9,background:currentPlan===plan.id?'var(--bg-secondary)':plan.color,border:currentPlan===plan.id?'1px solid var(--border)':'none',color:currentPlan===plan.id?'#8899b4':'#fff',fontWeight:700,cursor:currentPlan===plan.id?'default':'pointer',fontSize:14}}>
                  {currentPlan===plan.id ? '✓ Current Plan' : plan.cta}
                </button>
              </div>
            ))}
          </div>

          <div style={{background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:12,padding:'16px 20px',fontSize:13,color:'#8899b4'}}>
            💡 <strong style={{color:'#6366f1'}}>Testing mode:</strong> Plans are priced at ₹1–₹2/mo for evaluation. Full pricing will apply at launch.
          </div>
        </div>
      )}

      {tab === 'compare' && (
        <div style={{background:'var(--bg-card)',borderRadius:14,border:'1px solid var(--border)',overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{background:'var(--bg-secondary)'}}>
                <th style={{padding:'14px 20px',textAlign:'left',fontSize:13,color:'#8899b4',fontWeight:700}}>Feature</th>
                <th style={{padding:'14px 20px',textAlign:'center',fontSize:13,color:'#64748b',fontWeight:700}}>Free ₹0</th>
                <th style={{padding:'14px 20px',textAlign:'center',fontSize:13,color:'#6366f1',fontWeight:700}}>Starter ₹1</th>
                <th style={{padding:'14px 20px',textAlign:'center',fontSize:13,color:'#10b981',fontWeight:700}}>Pro ₹2 ⭐</th>
              </tr>
            </thead>
            <tbody>
              {PLAN_COMPARE.map((row,i)=>(
                <tr key={i} style={{borderTop:'1px solid var(--border)',background:i%2===0?'transparent':'rgba(255,255,255,0.01)'}}>
                  <td style={{padding:'12px 20px',fontSize:13,color:'var(--text-primary)',fontWeight:500}}>{row.feature}</td>
                  <td style={{padding:'12px 20px',textAlign:'center',fontSize:13,color:'#64748b'}}>{row.free}</td>
                  <td style={{padding:'12px 20px',textAlign:'center',fontSize:13,color:'#8899b4'}}>{row.starter}</td>
                  <td style={{padding:'12px 20px',textAlign:'center',fontSize:13,color:'#10b981',fontWeight:600}}>{row.pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'team' && (
        <div>
          {!org ? (
            <div style={{background:'var(--bg-card)',borderRadius:14,padding:24,border:'1px solid var(--border)',maxWidth:480}}>
              <h3 style={{margin:'0 0 8px'}}>Create your organization</h3>
              <p style={{color:'#8899b4',fontSize:13,marginBottom:16}}>Set up a team so you can invite members and assign roles like Editor, Viewer, Billing, or Monitor.</p>
              <form onSubmit={handleCreateOrg} style={{display:'flex',gap:10}}>
                <input className="form-control" value={orgName} onChange={e=>setOrgName(e.target.value)} placeholder="Organization name" required style={{flex:1}}/>
                <button type="submit" disabled={creatingOrg} style={{padding:'10px 20px',borderRadius:8,background:'#6366f1',border:'none',color:'#fff',fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                  {creatingOrg ? 'Creating...' : 'Create'}
                </button>
              </form>
            </div>
          ) : (
            <div>
              <div style={{marginBottom:20}}>
                <div style={{fontWeight:700,fontSize:16}}>{org.name}</div>
                <div style={{color:'#8899b4',fontSize:13}}>{org.plan} plan · your role: {role}</div>
              </div>

              {can('team') && (
                <div style={{background:'var(--bg-card)',borderRadius:14,padding:20,border:'1px solid var(--border)',marginBottom:20,maxWidth:640}}>
                  <h3 style={{margin:'0 0 12px',fontSize:15}}>Invite a team member</h3>
                  <form onSubmit={handleInvite} style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                    <input className="form-control" type="email" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="teammate@example.com" required style={{flex:'2 1 220px'}}/>
                    <select className="form-control" value={inviteRole} onChange={e=>setInviteRole(e.target.value)} style={{flex:'1 1 200px'}}>
                      {ROLE_OPTIONS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                    <button type="submit" disabled={inviting} style={{padding:'10px 20px',borderRadius:8,background:'#6366f1',border:'none',color:'#fff',fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                      {inviting ? 'Sending...' : 'Send Invite'}
                    </button>
                  </form>
                </div>
              )}

              <div style={{background:'var(--bg-card)',borderRadius:14,border:'1px solid var(--border)',overflow:'hidden'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'var(--bg-secondary)'}}>
                      <th style={{padding:'12px 18px',textAlign:'left',fontSize:12,color:'#8899b4',fontWeight:700}}>Email</th>
                      <th style={{padding:'12px 18px',textAlign:'left',fontSize:12,color:'#8899b4',fontWeight:700}}>Role</th>
                      <th style={{padding:'12px 18px',textAlign:'left',fontSize:12,color:'#8899b4',fontWeight:700}}>Status</th>
                      {can('team') && <th style={{padding:'12px 18px',textAlign:'right',fontSize:12,color:'#8899b4',fontWeight:700}}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => (
                      <tr key={m._id} style={{borderTop:'1px solid var(--border)'}}>
                        <td style={{padding:'12px 18px',fontSize:13}}>{m.email}</td>
                        <td style={{padding:'12px 18px',fontSize:13}}>
                          {m.role === 'owner' || !can('team') ? (
                            <span style={{textTransform:'capitalize'}}>{m.role}</span>
                          ) : (
                            <select className="form-control" value={m.role} onChange={e=>handleRoleChange(m._id, e.target.value)} style={{padding:'4px 8px',fontSize:12}}>
                              {ROLE_OPTIONS.map(r => <option key={r.id} value={r.id}>{r.id}</option>)}
                            </select>
                          )}
                        </td>
                        <td style={{padding:'12px 18px',fontSize:13}}>
                          <span style={{fontSize:11,padding:'2px 10px',borderRadius:12,background:m.status==='active'?'rgba(16,185,129,0.15)':'rgba(245,158,11,0.15)',color:m.status==='active'?'#10b981':'#f59e0b',fontWeight:700}}>
                            {m.status}
                          </span>
                        </td>
                        {can('team') && (
                          <td style={{padding:'12px 18px',textAlign:'right'}}>
                            {m.role !== 'owner' && (
                              <button onClick={()=>handleRemoveMember(m._id)} style={{padding:'6px 14px',borderRadius:6,background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',color:'#ef4444',fontWeight:600,cursor:'pointer',fontSize:12}}>
                                Remove
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'danger' && (
        <div style={{background:'rgba(239,68,68,0.05)',borderRadius:14,padding:24,border:'1px solid rgba(239,68,68,0.2)',maxWidth:500}}>
          <h3 style={{color:'#ef4444',margin:'0 0 16px'}}>⚠️ Danger Zone</h3>
          <div style={{marginBottom:16}}>
            <div style={{fontWeight:600,marginBottom:4}}>Log Out of All Devices</div>
            <div style={{fontSize:13,color:'#8899b4',marginBottom:12}}>This will invalidate all active sessions.</div>
            <button onClick={()=>{ localStorage.clear(); window.location.href='/login'; }} style={{padding:'9px 18px',borderRadius:8,background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',color:'#ef4444',fontWeight:700,cursor:'pointer'}}>
              Log Out of All Devices
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
